import { CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import React, { useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useAccount } from 'wagmi';

import { useExplorerUrl } from '../../../../hooks';
import { useStarStakingContract } from '../../../../hooks/stars-staking.hook';
import { Button } from '../../../core/button/Button';
import styles from './HarvestModal.module.css';
import { ModalFinishState } from './ModalFinishState.component';

const useStyles = makeStyles(() =>
  createStyles({
    loader: {
      color: 'white',
    },
  }),
);

enum ModalState {
  buy = 'BUY',
  finished = 'FINISHED',
}

export interface HarvestWithdrawModalProps {
  onClose: () => void;
  rewardToHarvest: number | undefined;
  withdrawAmount: number | undefined;
  poolId: number;
  depositId: number;
  getRewardInfo: () => void;
}

export const HarvestWithdrawModal: React.FC<HarvestWithdrawModalProps> = ({
  depositId,
  getRewardInfo,
  onClose,
  poolId,
  rewardToHarvest,
  withdrawAmount,
}) => {
  const classes = useStyles();
  const { address } = useAccount();
  const explorerUrl = useExplorerUrl();
  const { withdrawAndHarvest } = useStarStakingContract();
  const [submitting, setSubmitting] = useState(false);
  const [modalState, setModalState] = useState(ModalState.buy);
  const [transactionUrl, setTransactionUrl] = useState('');

  const handleClose = () => {
    if (submitting) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const txn = await withdrawAndHarvest(poolId, address!, depositId);
      if (!txn.success) {
        setSubmitting(false);
        toast.error(`Harvesting and Withdrawal Failed`);
        return;
      }

      const url = `${explorerUrl}/tx/${txn.transactionHash}`;

      setTransactionUrl(url);
      setModalState(ModalState.finished);

      toast.success(`Harvesting and Withdrawal Successsful!`);
      getRewardInfo();
    } catch (error) {
      setSubmitting(false);
      toast.error(`Harvesting and Withdrawal Failed`);
    }
    setSubmitting(false);
  };

  if (modalState === ModalState.finished) {
    return (
      <>
        <Toaster toastOptions={{ success: { duration: 5000 } }} />
        <ModalFinishState
          header="HARVESTING AND WITHDRAWAL COMPLETE"
          transactionUrl={transactionUrl}
          handleClose={handleClose}
        />
      </>
    );
  }

  return (
    <>
      <Toaster
        toastOptions={{
          success: {
            duration: 5000,
          },
        }}
      />
      <div
        className={`${styles.modalBG} relative w-full max-w-screen-sm lg:max-w-full flex flex-col gap-10 justify-center items-center`}
      >
        {submitting && (
          <div className={styles.starsLoading}>
            <CircularProgress className={`${classes.loader} mb-5`} size={85} />
            <div className={styles.description}>Awaiting Transaction</div>
          </div>
        )}
        <div className={styles.title + ' relative'}>
          <div
            className={styles.closeIcon}
            role="button"
            onKeyPress={onClose}
            tabIndex={0}
            onClick={onClose}
          >
            <CloseIcon />
          </div>
          HARVEST AND WITHDRAW
        </div>
        <div className="text-center">
          <div className={styles.earnedAmount}>
            {rewardToHarvest?.toLocaleString()} MOGUL
          </div>
          <div className={styles.caption}>REWARDS EARNED</div>
        </div>
        <div className="text-center">
          <div className={styles.earnedAmount}>
            {withdrawAmount?.toLocaleString()} MOGUL
          </div>
          <div className={styles.caption}>WITHDRAW AMOUNT</div>
        </div>
        <Button
          onClick={handleSubmit}
          className={
            styles.poolButton +
            ' w-5/6 flex justify-center items-center pointer'
          }
          disabled={(!withdrawAmount && !rewardToHarvest) || submitting}
        >
          CLAIM
        </Button>
      </div>
    </>
  );
};

export const createHarvestWithdrawModal =
  (
    rewardToHarvest: number | undefined,
    withdrawAmount: number | undefined,
    poolId: number,
    depositId: number,
    getRewardInfo: () => void,
  ) =>
  ({ hideModal }: { hideModal: () => void }) => {
    return (
      <HarvestWithdrawModal
        rewardToHarvest={rewardToHarvest}
        withdrawAmount={withdrawAmount}
        onClose={() => hideModal()}
        poolId={poolId}
        depositId={depositId}
        getRewardInfo={getRewardInfo}
      />
    );
  };
